import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { formatCurrency, formatDate, formatDateTime, getPaymentStatusColor, getInvoiceStatusColor } from '../utils/helpers';
import Loading from '../components/Loading';
import Alert from '../components/Alert';
import Modal from '../components/Modal';
import PaymentModal from '../components/PaymentModal';
import InvoiceItemsTable from '../components/InvoiceItemsTable';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, 
  Pencil, 
  Download, 
  Printer, 
  Mail, 
  Lock, 
  CreditCard, 
  Package, 
  Info, 
  Calculator, 
  CheckCircle,
  X,
  PlusCircle,
  Ban 
} from 'lucide-react';

const ViewInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);


  useEffect(() => {
    fetchInvoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

  // Auto-show payment modal - DISABLED (user prefers manual control)
  // useEffect(() => {
  //   if (invoice && !paymentPromptShown && invoice.status === 'active' &&
  //     !invoice.paymentDetails?.method && invoice.paymentStatus !== 'paid') {
  //     // Show after a short delay
  //     const timer = setTimeout(() => {
  //       setShowPaymentModal(true);
  //       setPaymentPromptShown(true);
  //     }, 1000);
  //     return () => clearTimeout(timer);
  //   }
  //


  const [products, setProducts] = useState([]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const [invoiceRes, productsRes] = await Promise.all([
        api.get(`/invoices/${id}`),
        api.get('/products')
      ]);
      setInvoice(invoiceRes.data.invoice);
      if (productsRes.data && productsRes.data.products) {
        setProducts(productsRes.data.products);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await api.get(`/invoices/${id}/download`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice-${invoice.invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download PDF');
    }
  };

  const handlePrintPDF = async () => {
    try {
      const response = await api.get(`/invoices/${id}/download`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const iframe = document.createElement('iframe');
      // Use visibility hidden instead of display none for better browser compatibility during print
      iframe.style.position = 'fixed';
      iframe.style.width = '0px';
      iframe.style.height = '0px';
      iframe.style.border = 'none';
      iframe.src = url;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        // Clean up after a delay
        setTimeout(() => {
          document.body.removeChild(iframe);
          window.URL.revokeObjectURL(url);
        }, 1000);
      };
    } catch (err) {
      setError('Failed to print PDF');
    }
  };

  const handleResendEmail = async () => {
    try {
      await api.post(`/invoices/${id}/resend-email`);
      setSuccess('Email sent successfully');
      fetchInvoice();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send email');
    }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      setError('Please provide a cancellation reason');
      return;
    }

    try {
      await api.post(`/invoices/${id}/cancel`, { reason: cancelReason });
      setSuccess('Invoice cancelled successfully');
      setShowCancelModal(false);
      fetchInvoice();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to cancel invoice');
    }
  };

  const handleMarkPaid = async (paymentData) => {
    try {
      await api.post(`/invoices/${id}/mark-paid`, paymentData);
      setSuccess('Payment details saved successfully!');
      setShowPaymentModal(false);
      fetchInvoice();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save payment details');
    }
  };

  if (loading) return <Loading />;
  if (!invoice) return <Alert type="error" message="Invoice not found" />;

  return (
    <div>
      <div className="page-header">
        <h1>Invoice Details</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-secondary" onClick={() => navigate('/invoice')} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ArrowLeft size={18} /> Back
          </button>
          {invoice.status === 'active' && !invoice.isLocked && (
            <button className="btn btn-primary" onClick={() => navigate(`/invoice/${id}/edit`)} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Pencil size={18} /> Edit Invoice
            </button>
          )}
          <button className="btn btn-primary" onClick={handleDownloadPDF} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Download size={18} /> Download PDF
          </button>
          <button className="btn btn-primary" onClick={handlePrintPDF} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Printer size={18} /> Print
          </button>
          {invoice.customerDetails?.email && (
            <button className="btn btn-secondary" onClick={handleResendEmail} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Mail size={18} /> Resend Email
            </button>
          )}
        </div>
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

      {/* Invoice Header */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <div>
            <h2>{invoice.invoiceNumber}</h2>
            <p>Date: {formatDate(invoice.invoiceDate)}</p>
            <p>Financial Year: {invoice.financialYear}</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div>
              <span className={`badge badge-${getInvoiceStatusColor(invoice.status)}`} style={{ fontSize: '14px', padding: '8px 12px' }}>
                {invoice.status.toUpperCase()}
              </span>
            </div>
            <div style={{ marginTop: '10px' }}>
              <span className={`badge badge-${getPaymentStatusColor(invoice.paymentStatus)}`} style={{ fontSize: '14px', padding: '8px 12px' }}>
                {invoice.paymentStatus.toUpperCase()}
              </span>
            </div>
            {invoice.isLocked && (
              <div style={{ marginTop: '10px', color: '#e74c3c', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px' }}>
                <Lock size={16} /> Locked
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Customer Details */}
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Info size={18} /> Customer Information
          </div>
        </div>
        <div className="form-row">
          <div>
            <strong>Name:</strong> {invoice.customerDetails.name}<br />
            <strong>Phone:</strong> {invoice.customerDetails.phone}<br />
            {invoice.customerDetails.email && (
              <><strong>Email:</strong> {invoice.customerDetails.email}<br /></>
            )}
          </div>
          <div>
            {invoice.customerDetails.address && (
              <><strong>Address:</strong> {invoice.customerDetails.address}<br /></>
            )}
            <strong>City:</strong> {invoice.customerDetails.city}<br />
            <strong>State:</strong> {invoice.customerDetails.state}<br />
            {invoice.customerDetails.pincode && (
              <><strong>PIN:</strong> {invoice.customerDetails.pincode}<br /></>
            )}
          </div>
          <div>
            {invoice.customerDetails.gstNumber && (
              <><strong>GST Number:</strong> {invoice.customerDetails.gstNumber}<br /></>
            )}
            <strong>Place of Supply:</strong> {invoice.customerDetails.placeOfSupply}<br />
            <strong>Tax Type:</strong> {invoice.isIGST ? 'IGST' : 'CGST + SGST'}
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Package size={18} /> Items
          </div>
        </div>
        <InvoiceItemsTable items={invoice.items} onItemsChange={() => { }} readOnly={true} products={products} />
      </div>

      {/* Totals */}
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Calculator size={18} /> Invoice Totals
          </div>
        </div>
        <table style={{ width: '100%', maxWidth: '500px', marginLeft: 'auto' }}>
          <tbody>
            <tr>
              <td><strong>Subtotal:</strong></td>
              <td style={{ textAlign: 'right' }}>{formatCurrency(invoice.subtotal)}</td>
            </tr>
            {invoice.isIGST ? (
              <tr>
                <td><strong>IGST:</strong></td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(invoice.taxBreakup.igst)}</td>
              </tr>
            ) : (
              <>
                <tr>
                  <td><strong>CGST:</strong></td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(invoice.taxBreakup.cgst)}</td>
                </tr>
                <tr>
                  <td><strong>SGST:</strong></td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(invoice.taxBreakup.sgst)}</td>
                </tr>
              </>
            )}
            <tr>
              <td><strong>Total Tax:</strong></td>
              <td style={{ textAlign: 'right' }}>{formatCurrency(invoice.taxBreakup.total)}</td>
            </tr>
            {invoice.roundOff !== 0 && (
              <tr>
                <td><strong>Round Off:</strong></td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(invoice.roundOff)}</td>
              </tr>
            )}
            <tr style={{ borderTop: '2px solid #333', fontSize: '20px' }}>
              <td><strong>Grand Total:</strong></td>
              <td style={{ textAlign: 'right' }}><strong>{formatCurrency(invoice.grandTotal)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Admin Actions */}
      {invoice.status === 'active' && !invoice.isLocked && (
        <div className="card no-print">
          <div className="card-header">Actions</div>
          <div style={{ display: 'flex', gap: '10px' }}>
            {invoice.paymentStatus !== 'paid' && (
              <button
                className="btn btn-primary"
                onClick={() => setShowPaymentModal(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <PlusCircle size={18} /> Add Payment Details
              </button>
            )}
            <button
              className="btn btn-danger"
              onClick={() => setShowCancelModal(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Ban size={18} /> Cancel Invoice
            </button>
          </div>
        </div>
      )}

      {/* Payment Details */}
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CreditCard size={18} /> Payment Information
          </div>
        </div>
        <div className="form-row">
          <div>
            <strong>Payment Status:</strong> {invoice.paymentStatus.toUpperCase()}<br />
            {invoice.paymentDetails?.method && (
              <><strong>Method:</strong> {invoice.paymentDetails.method.toUpperCase()}<br /></>
            )}

            {/* UPI Details */}
            {invoice.paymentDetails?.upiId && (
              <><strong>UPI ID:</strong> {invoice.paymentDetails.upiId}<br /></>
            )}

            {/* Cheque Details */}
            {invoice.paymentDetails?.chequeNumber && (
              <><strong>Cheque Number:</strong> {invoice.paymentDetails.chequeNumber}<br /></>
            )}
            {invoice.paymentDetails?.chequeDate && (
              <><strong>Cheque Date:</strong> {formatDate(invoice.paymentDetails.chequeDate)}<br /></>
            )}

            {/* Bank Details */}
            {invoice.paymentDetails?.bankName && (
              <><strong>Bank Name:</strong> {invoice.paymentDetails.bankName}<br /></>
            )}
            {invoice.paymentDetails?.accountNumber && (
              <><strong>Account Number:</strong> {invoice.paymentDetails.accountNumber}<br /></>
            )}
            {invoice.paymentDetails?.ifscCode && (
              <><strong>IFSC Code:</strong> {invoice.paymentDetails.ifscCode}<br /></>
            )}
            {invoice.paymentDetails?.transactionReference && (
              <><strong>Transaction Ref:</strong> {invoice.paymentDetails.transactionReference}<br /></>
            )}

            {/* Transaction ID */}
            {invoice.paymentDetails?.transactionId && (
              <><strong>Transaction ID:</strong> {invoice.paymentDetails.transactionId}<br /></>
            )}
          </div>
          <div>
            <strong>Paid Amount:</strong> {formatCurrency(invoice.paymentDetails?.paidAmount || 0)}<br />
            <strong>Due Amount:</strong> {formatCurrency(invoice.paymentDetails?.dueAmount || 0)}<br />
            {invoice.paymentDetails?.dueDate && (
              <><strong>Due Date:</strong> {formatDate(invoice.paymentDetails.dueDate)}<br /></>
            )}
            {invoice.paymentDetails?.paidDate && (
              <><strong>Paid Date:</strong> {formatDate(invoice.paymentDetails.paidDate)}<br /></>
            )}
          </div>
        </div>
      </div>

      {/* Notes and Terms */}
      <div className="card">
        {invoice.notes && (
          <>
            <div className="card-header">Notes</div>
            <p style={{ whiteSpace: 'pre-wrap', marginBottom: '15px' }}>{invoice.notes}</p>
          </>
        )}
        {invoice.termsAndConditions && (
          <>
            <div className="card-header">Terms & Conditions</div>
            <p style={{ whiteSpace: 'pre-wrap' }}>{invoice.termsAndConditions}</p>
          </>
        )}

        {/* Signature Block (Print Only, typically) */}
        <div className="print-footer" style={{ marginTop: '50px', display: 'flex', justifyContent: 'flex-end' }}>
          <div className="signature-box" style={{ textAlign: 'center' }}>
            <div style={{ height: '60px' }}></div> {/* Space for sign */}
            <div style={{ borderTop: '1px solid #000', width: '200px', paddingTop: '5px' }}>
              <strong>Authorized Signatory</strong>
            </div>
          </div>
        </div>
      </div>


      {/* Cancellation Details */}
      {invoice.status === 'cancelled' && invoice.cancellationDetails && (
        <div className="card" style={{ borderLeft: '4px solid #e74c3c' }}>
          <div className="card-header">Cancellation Details</div>
          <p><strong>Reason:</strong> {invoice.cancellationDetails.reason}</p>
          <p><strong>Cancelled At:</strong> {formatDateTime(invoice.cancellationDetails.cancelledAt)}</p>
        </div>
      )}

      {/* Email Status */}
      {invoice.emailSent && (
        <div className="card no-print">
          <div className="card-header">Email Status</div>
          <p style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CheckCircle size={16} style={{ color: '#10b981' }} /> Email sent at {formatDateTime(invoice.emailSentAt)}
          </p>
          {invoice.emailRetryCount > 0 && (
            <p>Retry count: {invoice.emailRetryCount}</p>
          )}
        </div>
      )}


      {/* Cancel Modal */}
      <Modal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title="Cancel Invoice"
        footer={
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button 
              className="btn btn-secondary" 
              onClick={() => setShowCancelModal(false)}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <X size={18} /> Close
            </button>
            <button 
              className="btn btn-danger" 
              onClick={handleCancel}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Ban size={18} /> Confirm Cancel
            </button>
          </div>
        }
      >
        <div className="form-group">
          <label className="form-label">Cancellation Reason *</label>
          <textarea
            className="form-control"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            rows="4"
            placeholder="Please provide a reason for cancellation"
            required
          />
        </div>
      </Modal>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        invoice={invoice}
        onSubmit={handleMarkPaid}
      />
    </div>
  );
};

export default ViewInvoice;
